/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { AscDesc, StreamChat } from 'stream-chat';
import 'stream-chat-react/dist/css/v2/index.css';
import { styled } from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import SwipeToDelete from 'react-swipe-to-delete-ios';
import CommunityConnector from '../../../models/CommunityModels';
import useAsyncAction, { useWindowDimensions } from '../../../commonComponents/Hooks';
import { chatPageUrlRoot } from '../../../core/constants';
import { getFormattedDate } from '../../../lib/utils';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import {
	borderLightGrey,
	greyColour,
	greyBackground,
	labelGreyColour,
	slateGrey,
	superLightGreyColour,
	tabletPortraitMinWidth,
	whiteColour,
	lightGrayish,
	cardInnerGap
} from '../../cssConstants';
import useClientContextStore from '../../../zustandStore/clientContextStore';
import useChatStore from '../../../zustandStore/chatStore';
import useBlockedUsersStore from '../../../zustandStore/blockedUsersStore';

import SearchInput from '../../../commonComponents/appForm/SearchInput';
import { NotificationCircle } from '../notification/sections/NotificationListSection';
import { NoMarginEmptyCard } from '../common/EmptyCard';
import TrashRedSvg from '../../../images/trashRed.svg';
import { invalidateNotificationQueries } from '../../../core/api/pushNotification';

const ChatsSubPage = () => {
	const navigate = useNavigateWithAppName();
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const chatClient = useChatStore((state) => state.chatClient);
	const blockedUserIds = useBlockedUsersStore((state) => state.blockedUserIds);
	const { width: screenWidth } = useWindowDimensions();
	const queryClient = useQueryClient();
	const [searchQuery, setSearchQuery] = React.useState('');
	const [searchActive, setSearchActive] = React.useState(false);
	const [channels, setChannels] = React.useState<any[]>();
	const [searchResultChannelIds, setSearchResultChannelIds] = React.useState<Set<string>>(new Set());
	const [filteredChannels, setFilteredChannels] = React.useState<any[]>();

	const performAsyncAction = useAsyncAction();

	React.useEffect(() => {
		if (chatClient && clientDetails) {
			performAsyncAction(async () => {
				const filters = getFilters(clientDetails.userKey);
				const channelList = await chatClient.queryChannels(filters, sort);
				setChannels(channelList);
			});
		}
	}, [chatClient, clientDetails]);

	// Async search for message content
	React.useEffect(() => {
		if (!searchActive || !chatClient || !clientDetails || searchQuery.length < 3) {
			setSearchResultChannelIds(new Set());
			return;
		}

		performAsyncAction(async () => {
			const channelFilters = { type: chatType };
			const messageFilters = { text: { $autocomplete: searchQuery.toLowerCase() } };

			const searchResults = await chatClient.search(channelFilters, messageFilters, {
				// sorted by relevance in descending order, then updated_at in descending order
				sort: [{ relevance: -1 }, { updated_at: -1 }],
				limit: 20
			});

			// Each message has a cid (channel ID) in format "messaging:channelId"
			// Split by ':' and take the second part to get the actual channel ID
			const channelIds = new Set(searchResults.results.map((result) => result.message.cid.split(':')[1]));
			setSearchResultChannelIds(channelIds);
		});
	}, [searchActive, searchQuery, chatClient, clientDetails]);

	// Filter channels based on search query and results
	React.useEffect(() => {
		if (!channels || !clientDetails) return;

		if (!searchActive) {
			setFilteredChannels(channels);
			return;
		}

		const query = searchQuery.toLowerCase();
		const filtered = channels.filter((channel) => {
			const channelName = getChannelName(channel, clientDetails?.userKey).toLowerCase();

			// Search in channel name or message content (from async search results)
			return channelName.includes(query) || searchResultChannelIds.has(channel.id);
		});
		setFilteredChannels(filtered);
	}, [searchActive, searchQuery, channels, clientDetails, searchResultChannelIds]);

	/* Keep these codes for future use - creating new chat
	React.useEffect(() => {
		if (clientDetails) {
			performAsyncAction(async () => {
				const connectors = await getCommunityConnectors(signal);
				setCommunityConnectors(connectors);
			});
		}
	}, [clientDetails]);

	const handleAddChatButtonClick = () => {
		setShowConnectorsPage(true);
	};

	const mutation = useUpdateOrCreateChatUsers(async (selectedConnectors: CommunityConnector[]) => {
		createAndNavigateToChat(chatClient, selectedConnectors, navigate);
	});

	const handleAddNewChat = (selectedConnectors: CommunityConnector[]) => {
		if (selectedConnectors.length === 0) return;

		const appUserConnector = communityConnectors.find((c) => c.userId === clientDetails?.userKey);
		const channelConnectors = [appUserConnector, ...selectedConnectors].filter(Boolean);
		mutation.mutate(channelConnectors);
		setShowConnectorsPage(false);
	};
	*/

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchQuery(value);
		setSearchActive(value.length >= 3);
	};

	const handleChannelClick = (channelId: string) => {
		// Invalidate queries to reload the notification unread count
		invalidateNotificationQueries(queryClient);

		const newChatPageUrl = `${chatPageUrlRoot}/${channelId}`;
		navigate(newChatPageUrl);
	};

	const handleDeleteChannel = async (channelId: string) => {
		const channel = channels.find((c) => c.id === channelId);
		if (channel) {
			await performAsyncAction(async () => {
				// Hide the channel for the current user instead of deleting it completely.
				// This also clears the history for the user.
				await channel.hide(null, true);
			});
			// Remove from local state
			setChannels((prev) => prev.filter((c) => c.id !== channelId));
		}
	};

	const getLastVisibleMessage = (channel: any) => {
		const messages = channel.state?.messages || [];
		for (let i = messages.length - 1; i >= 0; i -= 1) {
			const msg = messages[i];
			const senderId = msg?.user?.id;
			if (!senderId || !blockedUserIds.includes(senderId)) return msg;
		}
		return null;
	};

	const getChannelLastMessage = (channel: any) => {
		return getLastVisibleMessage(channel)?.text || 'No messages yet';
	};

	const getChannelLastMessageTime = (channel: any) => {
		const lastMessage = getLastVisibleMessage(channel);
		if (!lastMessage?.created_at) return '';

		return getFormattedDate(lastMessage.created_at);
	};

	const isChannelUnread = (channel: any) => {
		return channel.countUnread?.() > 0;
	};

	const getChannelAvatar = (channel: any) => {
		// First check if the channel has a custom image set (for group chats)
		if (channel.data?.image) {
			const channelName = channel.data?.name || 'Group Chat';
			return <AvatarImage src={channel.data.image} alt={channelName} />;
		}

		// Get other members (excluding current user)
		const members = Object.values(channel.state?.members || {}).filter(
			(member: any) => member.user?.id !== clientDetails?.userKey
		);

		if (members.length > 0) {
			// For group chats without custom image, use group initials if channel has a name
			if (members.length > 1 && channel.data?.name) {
				const initials = channel.data.name
					.split(' ')
					.map((word: string) => word[0])
					.join('')
					.toUpperCase()
					.slice(0, 2);
				return <AvatarFallback>{initials}</AvatarFallback>;
			}

			// For direct messages or unnamed groups, use first member's avatar
			const firstMember = members[0] as any;
			const avatarUrl = firstMember.user?.image;
			const userName = firstMember.user?.name || 'User';

			if (avatarUrl) {
				return <AvatarImage src={avatarUrl} alt={userName} />;
			}

			// Fallback to member initials
			const initials = userName
				.split(' ')
				.map((n: string) => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2);
			return <AvatarFallback>{initials}</AvatarFallback>;
		}

		// Default fallback
		return <AvatarFallback>??</AvatarFallback>;
	};

	return (
		<ChatWrapper>
			<StyledSearchInput value={searchQuery} onChange={handleSearchChange} placeholder="Search chat" />
			{filteredChannels && filteredChannels.length > 0 && (
				<ChannelContainer>
					{filteredChannels.map((channel) => (
						<SwipeToDelete
							key={channel.id}
							onDelete={() => handleDeleteChannel(channel.id)}
							height={95}
							deleteWidth={screenWidth * 0.4}
							transitionDuration={250}
							deleteColor={greyBackground}
							deleteComponent={<SwipeDeleteIcon src={TrashRedSvg} />}
						>
							<ChannelCard onClick={() => handleChannelClick(channel.id)}>
								{isChannelUnread(channel) && <NotificationCircle />}
								<ChannelAvatar>{getChannelAvatar(channel)}</ChannelAvatar>
								<ChannelInfo>
									<ChannelHeader>
										<ChannelTitle>{getChannelName(channel, clientDetails?.userKey)}</ChannelTitle>
										<ChannelTime>{getChannelLastMessageTime(channel)}</ChannelTime>
									</ChannelHeader>
									<ChannelLastMessage $isUnread={isChannelUnread(channel)}>
										{getChannelLastMessage(channel)}
									</ChannelLastMessage>
								</ChannelInfo>
							</ChannelCard>
						</SwipeToDelete>
					))}
				</ChannelContainer>
			)}
			{filteredChannels && filteredChannels.length === 0 && (
				<NoMarginEmptyCard title="All Clear" description="You have no messages right now." />
			)}
		</ChatWrapper>
	);

	/* Keep this component for future use - creating new chat
	<BottomButtonContainer>
				<AddButton onClick={handleAddChatButtonClick}>
					<PlusSign src={NewMessageBluePng} alt="Circled Plus Sign" />
				</AddButton>
	</BottomButtonContainer>
	<ConnectorsPage
		title="New Message"
		connectors={communityConnectors.filter((c) => c.userId !== clientDetails?.userKey)}
		onDoneClick={handleAddNewChat}
	/>
	*/
};

export default ChatsSubPage;

const sort = { last_message_at: -1 as AscDesc };
export const chatType = 'messaging';
export const groupChatIdPrefix = 'Group';

export const getChannelName = (channel: any, currentUserKey: any) => {
	// First check if the channel has a custom name set
	if (channel.data?.name) {
		return channel.data.name;
	}

	// Get other members (excluding current user)
	const otherMembers = Object.values(channel.state?.members || {}).filter(
		(member: any) => member.user?.id !== currentUserKey
	);

	if (otherMembers.length === 0) {
		return 'Empty Chat';
	}

	// Direct message (1 other person)
	if (otherMembers.length === 1) {
		const member = otherMembers[0] as any;
		return member.user?.name || 'Unknown User';
	}

	// Group chat (multiple people)
	const names = otherMembers.map((member: any) => member.user?.name || 'Unknown');

	if (names.length <= 3) {
		return names.join(', ');
	}

	// For large groups, show first 2 names + count
	return `${names.slice(0, 2).join(', ')} +${names.length - 2} others`;
};

export const createAndNavigateToChat = async (
	streamClient: StreamChat,
	connectors: CommunityConnector[],
	navigate: (url: string) => void
) => {
	const members = connectors.map((c) => c.userId);
	const channelId = createChannelId(members);
	const channel = streamClient.channel(chatType, channelId, { members });

	const newChatPageUrl = `${chatPageUrlRoot}/${channel.id}`;
	navigate(newChatPageUrl);
};

const getFilters = (userId: string) => {
	return { members: { $in: [userId] }, type: chatType };
};

export const createChannelId = (userIds: string[]) => {
	const channelIdMaxLength = 64;
	// Direct Message channel Id
	if (userIds.length === 2) return userIds.sort().join('_').slice(0, channelIdMaxLength);

	const guidParts = userIds.sort().map((userId) => userId.substring(0, 10));

	const result = [groupChatIdPrefix, Date.now(), ...guidParts].join('_').slice(0, channelIdMaxLength);
	return result;
};

const StyledSearchInput = styled(SearchInput)`
	margin-top: 0;
	margin-bottom: 18px;
`;

const ChannelContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const ChannelCard = styled.div`
	position: relative;
	background-color: ${whiteColour};
	border-radius: 12px;
	border: 1px solid ${borderLightGrey};
	box-shadow: 0 4px 16px 2px ${lightGrayish};
	padding: 21px 10px;
	cursor: pointer;
	transition: transform 0.2s ease;
	display: flex;
	align-items: flex-start;
	gap: ${cardInnerGap};
	height: 95px;

	&:hover {
		transform: translateY(-1px);
	}
`;

const ChannelAvatar = styled.div`
	flex-shrink: 0;
	width: 48px;
	height: 48px;
`;

const AvatarImage = styled.img`
	width: 48px;
	height: 48px;
	border-radius: 50%;
	object-fit: cover;
`;

const AvatarFallback = styled.div`
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${slateGrey};
	font-size: 16px;
	font-weight: 500;
`;

const ChannelInfo = styled.div`
	flex: 1;
	min-width: 0; /* Allows text to truncate properly */
`;

const ChannelHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 3px;
	position: relative;
`;

const ChannelTitle = styled.p`
	color: ${greyColour};
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.1px;
	margin: 0;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 16px;
		line-height: 24px;
	}
`;

const ChannelTime = styled.p`
	font-size: 11px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.5px;
	color: #9ca3af;
	margin: 0;
	white-space: nowrap;
`;

const ChannelLastMessage = styled.p<{ $isUnread?: boolean }>`
	color: ${({ $isUnread }) => ($isUnread ? greyColour : superLightGreyColour)};
	font-size: 12px;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0.4px;
	margin: 0;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 14px;
		line-height: 20px;
	}
`;

const ChatWrapper = styled.div`
	.str-chat__channel-preview-messenger {
		background: none;
		border-bottom: 2px solid #f0f0f0;
	}

	.str-chat__avatar-fallback {
		color: ${slateGrey} !important;
		background-color: #e5e7eb !important;
	}

	.str-chat__channel-search .str-chat__channel-search-bar {
		padding: 0;
		height: inherit;
	}

	.str-chat__channel-preview {
		padding-left: 0;
		padding-right: 0;
	}

	.str-chat__channel-search .str-chat__channel-search-bar .str-chat__channel-search-input--wrapper {
		border-radius: 12px;
		border: 1px solid ${borderLightGrey};
		background: #fff;
		height: 48px;
	}

	.str-chat__channel-search .str-chat__channel-search-bar .str-chat__channel-search-input--wrapper input {
		font-size: 16px;
		font-weight: 400;
		line-height: 20px;
		letter-spacing: 0.1px;

		&::placeholder {
			color: ${labelGreyColour};
			font-size: 14px;
			font-weight: 500;

			@media (min-width: ${tabletPortraitMinWidth}) {
				font-size: 18px;
			}
		}
	}

	.str-chat__channel-preview-messenger--name {
		color: ${greyColour};
		font-size: 14px;
		font-weight: 500;
		line-height: 20px;
		letter-spacing: 0.1px;
		padding-right: 3px;
		overflow: hidden;

		@media (min-width: ${tabletPortraitMinWidth}) {
			font-size: 16px;
		}
	}

	.str-chat__channel-preview-messenger--last-message {
		color: ${superLightGreyColour};
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.4px;
		overflow: hidden;
		display: flow;

		@media (min-width: ${tabletPortraitMinWidth}) {
			font-size: 14px;
			line-height: 18px;
		}
	}
`;

const SwipeDeleteIcon = styled.img`
	width: 36px;
	height: 36px;
`;

/* Keep this styling for future use - creating new chat
const BottomButtonContainer = styled.div`
	position: absolute;
	bottom: 138px; // 34+104 bottom nav. TODO: This is a temporary fix. Need to find a better way to position the button
	right: 25px;
`;

const AddButton = styled.button`
	all: unset;
	display: flex;
	flex-direction: column;
	align-items: center;
	cursor: pointer;
`;

const PlusSign = styled.img`
	width: 57px;
	height: auto;
`;
*/
